/**
 * Canonical URL path for a blog post.
 *
 * CommonJS on purpose, for the same reason as propertySlug.js: api/render.js
 * requires this from plain Node, and webpack resolves the named imports
 * unchanged for the frontend bundle.
 *
 * SHAPE: /blog/<slug>-<24-hex-id>
 *
 *   /blog/help-to-buy-the-good-the-bad-and-the-ugly-6106397ddfa16a0bbd6aec7d
 *
 * Same rule as properties: the id stays at the end and is the only thing
 * used for lookup, extracted with a regex rather than by position, so any
 * existing bare-id link - including ones already indexed - keeps resolving
 * forever. Nothing here forces those to redirect; the canonical tag in
 * postSeo() is what tells a search engine which form to prefer.
 *
 * WHY THIS ALSO EXPORTS findPostBySlug: server logs show requests for
 * addresses like /blog/help-to-buy-the-good-the-bad-and-the-ugly - a title
 * slug with no id at all, matching a real post title exactly. The site's own
 * blog links had only ever been the bare id, so that form came from
 * somewhere external (an older version of the site, or an inbound link), and
 * it 404'd in practice: BlogPost passed the whole slug to Post.findById,
 * Mongoose threw a CastError on the invalid ObjectId, and the page rendered
 * blank. findPostBySlug is what lets api/render.js 301 that legacy form to
 * the real post instead of continuing to serve nothing.
 */

const { slugify } = require("./propertySlug");

/** The path a post should live at. */
function postPath(post) {
    if (!post || !post._id) return "/blog";

    const id = String(post._id);
    const slug = slugify(post.title);

    return "/blog/" + (slug ? slug + "-" : "") + id;
}

/**
 * Pull the post id back out of a path segment.
 *
 * Matches the trailing 24-hex-character Mongo id, so it works for both
 * `<slug>-<id>` and a bare `<id>`. Returns null for anything else - a title
 * slug with no id, or a scanner probe - which the caller treats as not
 * resolvable rather than querying Mongo with something that is not an id.
 */
function extractPostId(segment) {
    const match = String(segment || "").match(/([0-9a-f]{24})$/i);
    return match ? match[1] : null;
}

/**
 * Every non-alphanumeric character removed, nothing inserted in its place.
 *
 * Used only for matching a legacy slug back to its post, never for building
 * a URL - propertySlug's slugify() (which expands "&" to "and" and joins
 * words with hyphens) stays the one algorithm that generates URLs, so
 * already-shipped links never change shape.
 *
 * The legacy requests logged in production do not follow that algorithm:
 * "Fixtures & Fittings- What are the rules?" arrives as
 * /blog/fixtures-fittings-what-are-the-rules - the "&" was dropped, not
 * expanded to "and". That segment cannot equal slugify(title) for any post
 * that has a "&" in its title, so matching on the exact algorithm this site
 * uses would miss real requests for real posts. Comparing with every
 * separator character removed is immune to how the two sides tokenised
 * punctuation, and the only way it produces a wrong match on 19 posts is if
 * two titles collapse to the identical letters and digits.
 */
function looseKey(text) {
    return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Match a bare title slug (no id) against a list of posts. `posts` only
 * needs a `title` (and `_id`, to build the redirect target from the match).
 */
function findPostBySlug(segment, posts) {
    const target = looseKey(segment);
    if (!target || !Array.isArray(posts)) return null;

    return posts.find(function (p) {
        return looseKey(p.title) === target;
    }) || null;
}

module.exports = { postPath, extractPostId, findPostBySlug };
